import { PlatformLocation } from '@angular/common';
import { DestroyRef, inject, Injectable, InjectionToken } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NavigationCancel, NavigationEnd, NavigationError, Router } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { combineLatest, filter, map } from 'rxjs';
import {
  isAuthCallbackSearch,
  isSignedOutUrl,
  safeReturnPath,
  SignedOutReason,
  signedOutUrl,
} from '../utils/signed-out-utils/signed-out-utils';
import { SIGNED_OUT_CONFIG } from '../components/signed-out/signed-out.config';
import { SessionSyncService } from './session-sync.service';

/** Loads a page from scratch, so it boots with the login another tab just stored. */
export const SESSION_RELOAD = new InjectionToken<(url: string) => void>('SESSION_RELOAD', {
  providedIn: 'root',
  factory: () => (url) => location.replace(url),
});

// Sends the app to the signed-out page the moment the session is gone, instead of leaving the user on a page that no longer works.
@Injectable({ providedIn: 'root' })
export class SessionWatchService {
  readonly #auth = inject(AuthService);
  readonly #router = inject(Router);
  readonly #platformLocation = inject(PlatformLocation);
  readonly #destroyRef = inject(DestroyRef);
  readonly #sessionSync = inject(SessionSyncService);
  readonly #reload = inject(SESSION_RELOAD);
  readonly #startPath = inject(SIGNED_OUT_CONFIG, { optional: true })?.startPath ?? '/';

  readonly isOnSignedOutPage = toSignal(
    this.#router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(e => isSignedOutUrl(e.urlAfterRedirects)),
    ),
    { initialValue: isSignedOutUrl(this.#platformLocation.pathname) },
  );

  // Set while the way out is underway: a leave guard's failing API call reports the lost session again before the url
  // changes, and navigating again would cancel the way out and run that guard again, endlessly.
  #leaving = false;

  constructor() {
    // Only the way out itself ending reopens the door; the navigation it cut short also ends, with its own url.
    this.#router.events
      .pipe(
        filter(e => e instanceof NavigationEnd || e instanceof NavigationCancel || e instanceof NavigationError),
        filter(e => isSignedOutUrl(e.url)),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(() => (this.#leaving = false));
  }

  // Call once from each app's AppComponent.
  watch(): void {
    // Auth0 only reports isAuthenticated$ = false after loading ends, so wait for it.
    combineLatest([this.#auth.isLoading$, this.#auth.isAuthenticated$])
      .pipe(
        filter(([isLoading, isAuthenticated]) => !isLoading && !isAuthenticated),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(() => this.#leaveExpiredSession());

    // Token failures (refresh token gone, login_required, ...) land here while isAuthenticated$ may still be true.
    this.#auth.error$
      .pipe(filter(Boolean), takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => this.#leaveExpiredSession());

    // appState$ only emits once a login has come back from Auth0 in this tab: let the other tabs know.
    this.#auth.appState$
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => this.#sessionSync.announce('signed-in'));

    this.#sessionSync.messages$
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((message) => (message === 'signed-out' ? this.#followLogout() : this.#followLogin()));
  }

  toSignedOut(reason: SignedOutReason, returnTo?: string | null): void {
    this.#leaving = true;
    void this.#router.navigateByUrl(signedOutUrl(reason, returnTo), { replaceUrl: true });
  }

  // Another tab logged out: the shared login is gone here too, so don't wait for the next click to find out.
  #followLogout(): void {
    if (this.#leaving || isSignedOutUrl(this.#platformLocation.pathname)) {
      return;
    }
    this.toSignedOut('logout');
  }

  // Another tab logged in: a tab waiting on the signed-out page reopens the page it was on, logged in with the shared
  // login, without asking for the password again.
  #followLogin(): void {
    const { pathname, search } = this.#platformLocation;
    if (!isSignedOutUrl(pathname)) {
      return;
    }
    this.#reload(safeReturnPath(new URLSearchParams(search).get('returnTo')) ?? this.#startPath);
  }

  #leaveExpiredSession(): void {
    if (this.#leaving) {
      return;
    }
    // Read at the moment of the emission: the browser location changes over the app's lifetime.
    const { pathname, search } = this.#platformLocation;
    // On the signed-out page there is nothing to leave; mid-callback, Auth0's own errorPath navigation decides where to go.
    if (isSignedOutUrl(pathname) || isAuthCallbackSearch(search)) {
      return;
    }
    this.toSignedOut('expired', pathname + search);
  }
}
